import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { prepareDevice, disableDevicePush } from "../lib/devicePush";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setThemeState] = useState(localStorage.getItem("gsp_theme") || "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("gsp_theme", theme);
  }, [theme]);
  useEffect(() => { if (user?.theme) setThemeState(user.theme); }, [user?.theme]);
  const setTheme = (t) => { setThemeState(t); api.put("/auth/me/theme", { theme: t }).catch(() => {}); };

  useEffect(() => {
    const token = localStorage.getItem("gsp_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("gsp_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("gsp_token", data.token);
    setUser(data.user);
    return data.user;
  };

  useEffect(() => {
    if (!user?.id) return;
    const sync = () => { if (!document.hidden) prepareDevice(user.id).catch((e) => window.dispatchEvent(new CustomEvent("gsp:push-error", { detail: e.message }))); };
    sync();
    document.addEventListener("visibilitychange", sync);
    navigator.serviceWorker?.addEventListener("controllerchange", sync);
    return () => { document.removeEventListener("visibilitychange", sync); navigator.serviceWorker?.removeEventListener("controllerchange", sync); };
  }, [user?.id]);

  const logout = async () => {
    try { await disableDevicePush(); } catch { /* Browser worker is deactivated even if the server is offline. */ }
    localStorage.removeItem("gsp_token");
    setUser(null);
  };

  const hasModule = (m) => {
    if (!user) return false;
    if ((user.excluded_modules || []).includes(m)) return false;
    return user.role === "super_admin" || (user.modules || []).includes(m);
  };

  const setUiPref = (key, value) => {
    setUser((u) => (u ? { ...u, ui_prefs: { ...(u.ui_prefs || {}), [key]: value } } : u));
    api.put("/auth/me/ui-preferences", { [key]: value }).catch(() => {});
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, hasModule, theme, setTheme, setUiPref }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
